/**
 * @module features/profile/components/ProfileEditForm
 * @description Forms for managing personal contact details and password changes.
 */

import * as React from "react";
import { useForm } from "react-hook-form";
import { User, Lock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUpdateProfile, useChangePassword } from "../api/profile-api";
import { ProfileDto } from "@/types/profile";
import { AppError } from "@/lib/api-client";

interface ProfileEditFormProps {
  profile: ProfileDto;
  tab?: "all" | "general" | "password";
}

interface ProfileFields {
  name: string;
  country_code: string;
  contact_number: string;
}

interface PasswordFields {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export function ProfileEditForm({ profile, tab = "all" }: ProfileEditFormProps) {
  // Profile update form
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors, isDirty: isProfileDirty },
  } = useForm<ProfileFields>({
    defaultValues: {
      name: profile.name,
      country_code: profile.country_code || "+1",
      contact_number: profile.contact_number || "",
    },
  });

  const updateProfileMutation = useUpdateProfile();
  const [profileSuccess, setProfileSuccess] = React.useState(false);
  const [profileError, setProfileError] = React.useState<string | null>(null);

  const onUpdateProfile = async (values: ProfileFields) => {
    setProfileSuccess(false);
    setProfileError(null);

    try {
      await updateProfileMutation.mutateAsync({
        name: values.name.trim(),
        country_code: values.country_code.trim(),
        contact_number: values.contact_number.trim(),
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 4000);
    } catch (err: any) {
      if (err instanceof AppError) {
        setProfileError(err.errorDetails);
      } else {
        setProfileError("Failed to update profile details.");
      }
    }
  };

  // Password change form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    watch: watchPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFields>({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const changePasswordMutation = useChangePassword();
  const [passwordSuccess, setPasswordSuccess] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  const newPasswordValue = watchPassword("new_password");

  const onChangePassword = async (values: PasswordFields) => {
    setPasswordSuccess(false);
    setPasswordError(null);

    try {
      await changePasswordMutation.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      resetPassword();
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 4000);
    } catch (err: any) {
      if (err instanceof AppError) {
        setPasswordError(err.errorDetails);
      } else {
        setPasswordError("Failed to change password. Please check your credentials.");
      }
    }
  };

  const showGeneral = tab === "all" || tab === "general";
  const showPassword = tab === "all" || tab === "password";

  return (
    <div className={tab === "all" ? "grid grid-cols-1 lg:grid-cols-2 gap-6" : "w-full"}>
      {/* ----------------------------------------------------------------- */}
      {/* 1. Personal Information */}
      {/* ----------------------------------------------------------------- */}
      {showGeneral && (
        <Card className="border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 shadow-xs rounded-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted/60 dark:bg-zinc-900 border border-border/60">
              <User className="h-4.5 w-4.5 text-foreground shrink-0" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold">Personal Details</CardTitle>
              <CardDescription className="text-xs text-muted-foreground mt-0.5">
                Update display name and contact phone number
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmitProfile(onUpdateProfile)}
            className="space-y-4"
          >
            {profileSuccess && (
              <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-success-soft text-success border border-success/20 animate-fade-in">
                <CheckCircle2 className="h-4 w-4" />
                <span>Profile details saved successfully.</span>
              </div>
            )}
            {profileError && (
              <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
                <AlertCircle className="h-4 w-4" />
                <span>{profileError}</span>
              </div>
            )}

            {/* Readonly Email */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">
                Email Address (Permanent ID)
              </label>
              <Input
                value={profile.email_id}
                disabled
                className="h-10 text-sm bg-muted/50 font-mono text-muted-foreground"
              />
            </div>

            {/* Full Name */}
            <div className="space-y-1">
              <label
                htmlFor="profile_name"
                className="text-xs font-medium text-foreground"
              >
                Full Name
              </label>
              <Input
                id="profile_name"
                {...registerProfile("name", { required: "Name is required." })}
                className="h-10 text-sm"
              />
              {profileErrors.name && (
                <p className="text-[11px] text-destructive">
                  {profileErrors.name.message}
                </p>
              )}
            </div>

            {/* Contact details */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1 col-span-1">
                <label
                  htmlFor="profile_country_code"
                  className="text-xs font-medium text-foreground"
                >
                  Code
                </label>
                <Input
                  id="profile_country_code"
                  placeholder="+1"
                  {...registerProfile("country_code")}
                  className="h-10 text-sm font-mono"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <label
                  htmlFor="profile_contact"
                  className="text-xs font-medium text-foreground"
                >
                  Contact Number
                </label>
                <Input
                  id="profile_contact"
                  placeholder="555-0199"
                  {...registerProfile("contact_number")}
                  className="h-10 text-sm font-mono"
                />
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                type="submit"
                disabled={!isProfileDirty || updateProfileMutation.isPending}
                className="min-h-[44px] sm:min-h-[36px]"
              >
                {updateProfileMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* 2. Change Password */}
      {/* ----------------------------------------------------------------- */}
      {showPassword && (
        <Card className="border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 shadow-xs rounded-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted/60 dark:bg-zinc-900 border border-border/60">
              <Lock className="h-4.5 w-4.5 text-foreground shrink-0" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold">Change Password</CardTitle>
              <CardDescription className="text-xs text-muted-foreground mt-0.5">
                Ensure your password is at least 8 characters
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmitPassword(onChangePassword)}
            className="space-y-4"
          >
            {passwordSuccess && (
              <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-success-soft text-success border border-success/20 animate-fade-in">
                <CheckCircle2 className="h-4 w-4" />
                <span>Password updated successfully.</span>
              </div>
            )}
            {passwordError && (
              <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
                <AlertCircle className="h-4 w-4" />
                <span>{passwordError}</span>
              </div>
            )}

            <div className="space-y-1">
              <label
                htmlFor="current_password"
                className="text-xs font-medium text-foreground"
              >
                Current Password
              </label>
              <Input
                id="current_password"
                type="password"
                autoComplete="current-password"
                {...registerPassword("current_password", {
                  required: "Current password is required.",
                })}
                className="h-10 text-sm font-mono"
              />
              {passwordErrors.current_password && (
                <p className="text-[11px] text-destructive">
                  {passwordErrors.current_password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="new_password"
                className="text-xs font-medium text-foreground"
              >
                New Password
              </label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                {...registerPassword("new_password", {
                  required: "New password is required.",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters.",
                  },
                })}
                className="h-10 text-sm font-mono"
              />
              {passwordErrors.new_password && (
                <p className="text-[11px] text-destructive">
                  {passwordErrors.new_password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="confirm_password"
                className="text-xs font-medium text-foreground"
              >
                Confirm New Password
              </label>
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                {...registerPassword("confirm_password", {
                  required: "Please confirm your new password.",
                  validate: (value) =>
                    value === newPasswordValue || "Passwords do not match.",
                })}
                className="h-10 text-sm font-mono"
              />
              {passwordErrors.confirm_password && (
                <p className="text-[11px] text-destructive">
                  {passwordErrors.confirm_password.message}
                </p>
              )}
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="min-h-[44px] sm:min-h-[36px]"
              >
                {changePasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Password"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
